import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Input,
  Table,
  Card,
  Tag,
  Avatar,
  Typography,
  Spin,
  Flex,
  Space,
  Row,
  Col,
  Pagination,
  Collapse,
  Select,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  DownOutlined,
  UpOutlined,
  ReloadOutlined,
  BankOutlined,
  UserOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import DashboardService from "../../service";
import SubdealerModal from "../../components/subdealers/SubdealerModal";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../i18n/TranslationContext";
import { useNotification } from "../../hooks/useNotification";
import CreatorInfo from "../../components/CreatorInfo";

const UserPage = () => {
  const { user: currentUser, hasPermission, canManageUser, canCreateSubdealer } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { showSuccess, showError } = useNotification();
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // UI state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editingSubdealer, setEditingSubdealer] = useState(null);
  
  // Search form state
  const [searchForm, setSearchForm] = useState({
    quickSearch: '',
    name: '',
    user_name: '',
    user_id: '',
    employee_name: '',
    employee_last_name: '',
    employee_phone: '',
    package_slug: '',
    copart_on: '',
    iaai_on: '',
    manheim_on: '',
    prebid: '',
    live_bid: '',
    unlimited: '',
    log_out: ''
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const debounceRef = useRef();

  // Filter users based on current user's role and permissions
  const filterUsersByRole = (userData) => {
    if (!currentUser) return [];
    
    // Super admin sees all subdealers
    if (currentUser.role === 'super_admin') {
      return userData;
    }
    
    // Managers see only subdealers in their branch
    if (currentUser.role === 'manager') {
      const managerUserId = currentUser.user_id || currentUser.id || currentUser.managerId;
      return userData.filter(u => 
        (u.branch_id === currentUser.branchId || u.branchId === currentUser.branchId) &&
        (u.managerId === managerUserId || u.manager_id === managerUserId)
      );
    }
    
    // Dealers see only their subdealers
    if (currentUser.role === 'dealer') {
      const dealerUserId = currentUser.user_id || currentUser.id || currentUser.dealerId;
      return userData.filter(u => 
        u.dealerId === dealerUserId || 
        u.dealer_id === dealerUserId ||
        u.dealerId === currentUser.user_id ||
        u.dealer_id === currentUser.user_id
      );
    }
    
    // Subdealers see only themselves
    if (currentUser.role === 'subdealer') {
      return userData.filter(u => u.id === currentUser.id);
    }
    
    return [];
  };

  // Load users with pagination
  const loadUsers = useCallback(async (page = 1, isNewSearch = false, forceSearchFilters = null) => {
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      let data;
      
      // Use forceSearchFilters if provided, otherwise use current state
      const currentSearchFilters = forceSearchFilters !== null ? forceSearchFilters : searchFilters;
      const shouldSearch = forceSearchFilters !== null 
        ? Object.keys(forceSearchFilters).length > 0 
        : (isSearching && Object.keys(searchFilters).length > 0);
      
      if (shouldSearch) {
        // Use search endpoint with pagination
        console.log('Using search endpoint with filters:', currentSearchFilters);
        data = await DashboardService.searchUsers({
          ...currentSearchFilters,
          page,
          limit: 20,
          sortBy,
          sortOrder
        });
      } else {
        // Use role-specific endpoint (auto-filtered by backend)
        console.log('Using role-specific subdealers endpoint');
        // This endpoint automatically filters based on user role:
        // - Super admin sees all subdealers
        // - Manager sees branch subdealers
        // - Dealer sees their subdealers
        data = await DashboardService.getSubdealers(page, 20, sortBy, sortOrder);
      }

      // Handle both paginated and legacy response formats
      const usersList = Array.isArray(data.users) ? data.users : (Array.isArray(data) ? data : []);
      const paginationData = data.pagination;

      // For role-based endpoints (getSubdealers), backend already filters correctly
      // Only apply client-side filtering for search results or legacy endpoints
      let filteredUsers;
      if (shouldSearch) {
        // Apply role-based filtering for search results
        filteredUsers = filterUsersByRole(usersList);
      } else {
        // Backend already filtered, use users as-is
        filteredUsers = usersList;
      }
      
      setUsers(filteredUsers);
      
      if (paginationData) {
        setPagination(paginationData);
        setCurrentPage(paginationData.currentPage);
      } else {
        // Fallback for non-paginated response
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalUsers: usersList.length,
          hasNextPage: false,
          hasPrevPage: false,
          limit: usersList.length
        });
        setCurrentPage(1);
      }

         } catch (err) {
       console.error('Error loading users:', err);
       setError(err.message || 'Failed to load users. Please try again later.');
       
       // Fallback to loadAllUsers if pagination endpoint doesn't exist
       if (err.message.includes('Failed to load users') || err.message.includes('Failed to search users')) {
         try {
           const fallbackData = await DashboardService.loadAllUsers();
           let filteredData = fallbackData;
           
                       // Apply client-side filtering if searching
            if (isSearching && Object.keys(searchFilters).length > 0) {
              filteredData = fallbackData.filter(user => {
                // Quick search across multiple fields
                if (searchFilters.quickSearch) {
                  const searchTerm = searchFilters.quickSearch.toLowerCase();
                  return (
                    user.name?.toLowerCase().includes(searchTerm) ||
                    user.user_name?.toLowerCase().includes(searchTerm) ||
                    user.user_id?.toLowerCase().includes(searchTerm) ||
                    user.employee_info?.name?.toLowerCase().includes(searchTerm) ||
                    user.employee_info?.last_name?.toLowerCase().includes(searchTerm) ||
                    user.employee_info?.phone?.toLowerCase().includes(searchTerm)
                  );
                }
                
                // Advanced search filters - check each field individually
                let matches = true;
                
                if (searchFilters.name && !user.name?.toLowerCase().includes(searchFilters.name.toLowerCase())) {
                  matches = false;
                }
                if (searchFilters.user_name && !user.user_name?.toLowerCase().includes(searchFilters.user_name.toLowerCase())) {
                  matches = false;
                }
                if (searchFilters.user_id && !user.user_id?.toLowerCase().includes(searchFilters.user_id.toLowerCase())) {
                  matches = false;
                }
                if (searchFilters.employee_name && !user.employee_info?.name?.toLowerCase().includes(searchFilters.employee_name.toLowerCase())) {
                  matches = false;
                }
                if (searchFilters.employee_last_name && !user.employee_info?.last_name?.toLowerCase().includes(searchFilters.employee_last_name.toLowerCase())) {
                  matches = false;
                }
                if (searchFilters.employee_phone && !user.employee_info?.phone?.toLowerCase().includes(searchFilters.employee_phone.toLowerCase())) {
                  matches = false;
                }
                if (searchFilters.copart_on && user.copart_on?.toString() !== searchFilters.copart_on) {
                  matches = false;
                }
                if (searchFilters.iaai_on && user.iaai_on?.toString() !== searchFilters.iaai_on) {
                  matches = false;
                }
                if (searchFilters.manheim_on && user.manheim_on?.toString() !== searchFilters.manheim_on) {
                  matches = false;
                }
                if (searchFilters.prebid && user.prebid?.toString() !== searchFilters.prebid) {
                  matches = false;
                }
                if (searchFilters.live_bid && user.live_bid?.toString() !== searchFilters.live_bid) {
                  matches = false;
                }
                if (searchFilters.unlimited && user.unlimited?.toString() !== searchFilters.unlimited) {
                  matches = false;
                }
                if (searchFilters.package_slug && !user.package_slug?.toLowerCase().includes(searchFilters.package_slug.toLowerCase())) {
                  matches = false;
                }
                
                return matches;
              });
            }
           
           // Apply client-side sorting
           if (sortBy && filteredData.length > 0) {
             filteredData.sort((a, b) => {
               let aVal = a[sortBy];
               let bVal = b[sortBy];
               
               // Handle nested employee_info fields
               if (sortBy === 'employee_name') {
                 aVal = a.employee_info?.name;
                 bVal = b.employee_info?.name;
               }
               
               // Handle null/undefined values
               if (!aVal) aVal = '';
               if (!bVal) bVal = '';
               
               // Convert to strings for comparison
               aVal = aVal.toString().toLowerCase();
               bVal = bVal.toString().toLowerCase();
               
               if (sortOrder === 'asc') {
                 return aVal.localeCompare(bVal);
               } else {
                 return bVal.localeCompare(aVal);
               }
             });
           }
           
                     // Apply role-based filtering first
          const roleFilteredData = filterUsersByRole(filteredData);
          
          // Apply client-side pagination
          const itemsPerPage = 20;
          const startIndex = (page - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          const paginatedData = roleFilteredData.slice(startIndex, endIndex);
          const totalPages = Math.ceil(roleFilteredData.length / itemsPerPage);
          
          setUsers(paginatedData);
           setPagination({
             currentPage: page,
             totalPages,
             totalUsers: roleFilteredData.length,
             hasNextPage: page < totalPages,
             hasPrevPage: page > 1,
             limit: itemsPerPage
           });
           setError(''); // Clear error if fallback works
         } catch (fallbackErr) {
           console.error('Fallback also failed:', fallbackErr);
         }
       }
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [loading, isSearching, searchFilters, sortBy, sortOrder]);

  // Handle URL search parameter on mount
  useEffect(() => {
    const urlSearch = searchParams.get("search");
    if (urlSearch && urlSearch.length) {
      setSearchForm(prev => ({ ...prev, quickSearch: urlSearch }));
      setSearchFilters({ quickSearch: urlSearch });
      setIsSearching(true);
    }
  }, [searchParams]);

  // Initial load
  useEffect(() => {
    loadUsers(1, true);
  }, []);


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Debounced search function for quick search only
  const debouncedQuickSearch = useCallback((searchTerm) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Check if search term is still valid when timeout executes
      if (searchTerm && searchTerm.trim() !== '') {
        const filters = { quickSearch: searchTerm.trim() };
        setSearchFilters(filters);
        setIsSearching(true);
        setCurrentPage(1);
        // Pass filters directly to avoid state timing issues
        loadUsers(1, true, filters);
        
        // Update URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('search', searchTerm.trim());
        setSearchParams(newSearchParams);
      } else {
        // If search term is empty, clear search and show all results
        clearSearch();
      }
    }, 300);
  }, [loadUsers, searchParams, setSearchParams]);

  // Handle quick search
  const handleQuickSearch = (value) => {
    setSearchForm(prev => ({ ...prev, quickSearch: value }));
    
    // Clear any existing timeout
    clearTimeout(debounceRef.current);
    
    if (value && value.trim() !== '') {
      debouncedQuickSearch(value.trim());
    } else {
      // Immediately clear search when field is empty
      clearSearch();
    }
  };

  // Handle advanced search - immediate execution, no debouncing
  const handleAdvancedSearch = () => {
    // Clear any pending quick search
    clearTimeout(debounceRef.current);
    
    // Build filters from form, excluding quickSearch and empty values
    const filters = {};
    Object.entries(searchForm).forEach(([key, value]) => {
      if (key !== 'quickSearch' && value && value.toString().trim() !== '') {
        filters[key] = value.toString().trim();
      }
    });

    console.log('Advanced search filters:', filters); // Debug log

    if (Object.keys(filters).length > 0) {
      setSearchFilters(filters);
      setIsSearching(true);
      setCurrentPage(1);
      // Pass filters directly to avoid state timing issues
      loadUsers(1, true, filters);
    } else {
      clearSearch();
    }
  };

  // Clear search
  const clearSearch = () => {
    // Cancel any pending search requests
    clearTimeout(debounceRef.current);
    
    setSearchFilters({});
    setIsSearching(false);
    setCurrentPage(1);
    setSearchForm({
      quickSearch: '',
      name: '',
      user_name: '',
      user_id: '',
      employee_name: '',
      employee_last_name: '',
      employee_phone: '',
      package_slug: '',
      copart_on: '',
      iaai_on: '',
      manheim_on: '',
      prebid: '',
      live_bid: '',
      unlimited: '',
      log_out: ''
    });
    
    // Clear URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('search');
    setSearchParams(newSearchParams);
    
    // Pass empty filters to explicitly use pagination endpoint
    loadUsers(1, true, {});
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadUsers(page);
  };

  // Handle sorting
  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortBy(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
    loadUsers(1, true);
  };

  // Modal handlers
  const handleAddUserDialogOpen = () => {
    setEditingSubdealer(null);
    setAddUserOpen(true);
  };

  const handleAddUserDialogClose = () => {
    setAddUserOpen(false);
    setEditingSubdealer(null);
    // Refresh data after adding/editing
    loadUsers(currentPage);
  };

  const handleEditUser = (subdealer) => {
    setEditingSubdealer(subdealer);
    setAddUserOpen(true);
  };

  const handleSaveSubdealer = async (formData, editingSubdealer) => {
    try {
      // Get branch and dealer data from API loaded data
      const branchesData = await DashboardService.getBranches();
      const dealersData = await DashboardService.getUsersByRole('dealer');
      const managersData = await DashboardService.getManagers(1, 100, 'createdAt', 'desc');
      const branch = branchesData.branches?.find(b => b.id === parseInt(formData.branchId));
      // Find dealer by user_id (USER format)
      const dealer = dealersData.users?.find(d => d.user_id === formData.dealerId);
      // Find manager by user_id (USER format)
      const manager = managersData.users?.find(m => m.user_id === formData.managerId);
      
      // Validate manager exists before submitting
      if (formData.managerId && !manager) {
        showError('Selected manager not found. Please select a valid manager.');
        return;
      }
      
      // Validate branch exists before submitting
      if (formData.branchId && !branch) {
        showError('Selected branch not found. Please select a valid branch.');
        return;
      }
      
      // Validate dealer exists before submitting (for subdealers)
      if (formData.dealerId && !dealer) {
        showError('Selected dealer not found. Please select a valid dealer.');
        return;
      }
    
      if (editingSubdealer) {
        // Update existing subdealer
        const updateData = {
          name: `${formData.firstName} ${formData.lastName}`, // Update full name when firstName/lastName changes
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          branchId: formData.branchId && formData.branchId !== '' ? parseInt(formData.branchId) : editingSubdealer.branchId,
          managerId: formData.managerId && formData.managerId !== '' ? formData.managerId : editingSubdealer.managerId, // Send user_id (USER format)
          dealerId: formData.dealerId && formData.dealerId !== '' ? formData.dealerId : editingSubdealer.dealerId, // Send user_id (USER format)
          subdealerId: editingSubdealer.subdealerId || editingSubdealer.id, // Preserve subdealerId
          copart_id: formData.copartId && formData.copartId !== '' ? formData.copartId : null,
          iaai_id: formData.iaaiId && formData.iaaiId !== '' ? formData.iaaiId : null,
          // Calculator Settings
          show_calculator: formData.show_calculator,
          show_discount: formData.show_discount,
          discountPercent: formData.discountPercent
        };
        
        // Include password only if provided
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }
        
        console.log('Updating subdealer with data:', updateData); // Debug log
        
        // Use user_id (primary), subdealerId, autoIncrementId, or id
        const subdealerId = editingSubdealer.user_id || editingSubdealer.subdealerId || editingSubdealer.autoIncrementId || editingSubdealer.id;
        console.log('Updating subdealer with ID:', subdealerId, 'from subdealer:', editingSubdealer); // Debug log
        
        if (!subdealerId) {
          showError('Cannot update subdealer: ID not found');
          return;
        }
        
        const result = await DashboardService.updateUser(subdealerId, updateData);
        
        if (result.success) {
          showSuccess('Subdealer updated successfully!');
          loadUsers(currentPage, true); // Reload data
        } else {
          // Handle specific error messages
          let errorMessage = result.error || result.message || 'Failed to update subdealer';
          
          // Provide more user-friendly error messages
          if (errorMessage.toLowerCase().includes('manager not found')) {
            errorMessage = 'Manager not found. Please select a valid manager.';
          } else if (errorMessage.toLowerCase().includes('branch not found')) {
            errorMessage = 'Branch not found. Please select a valid branch.';
          } else if (errorMessage.toLowerCase().includes('dealer not found')) {
            errorMessage = 'Dealer not found. Please select a valid dealer.';
          } else if (errorMessage.toLowerCase().includes('subdealer not found')) {
            errorMessage = 'Subdealer not found. The subdealer may have been deleted.';
          } else if (errorMessage.toLowerCase().includes('username') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Username already exists. Please choose a different username.';
          } else if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Email already exists. Please use a different email address.';
          }
          
          console.error('Failed to update subdealer:', result);
          showError(errorMessage);
          return; // Don't close modal if failed
        }
      } else {
        // Create new subdealer
        const subdealerData = {
          user_id: `USER${Date.now()}`,
          first_uuid: `UUID1-${Date.now()}`,
          second_uuid: `UUID2-${Date.now()}`,
          user_name: formData.username,
          password: formData.password,
          email: formData.email,
          role: 'subdealer',
          name: `${formData.firstName} ${formData.lastName}`, // Required field
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          branchId: formData.branchId && formData.branchId !== '' ? parseInt(formData.branchId) : null,
          branchName: branch?.name,
          managerId: formData.managerId && formData.managerId !== '' ? formData.managerId : null, // Send user_id (USER format)
          dealerId: formData.dealerId && formData.dealerId !== '' ? formData.dealerId : null, // Send user_id (USER format)
          dealerName: dealer?.name,
          copart_id: formData.copartId || null,
          iaai_id: formData.iaaiId || null,
          // Auction settings
          copart_on: true,
          iaai_on: true,
          manheim_on: false,
          prebid: true,
          live_bid: true,
          unlimited: false,
          // Limits
          bet_limit: {
            summ_one_bet: 0,
            limit: 0,
            count: 0,
            active_bids_limit: 0,
            active_bids_sum_limit: 0,
            active_bids_sum: 0
          },
          pay_limit: {
            count_limit: 0,
            count_pay: 0
          },
          // Settings
          lots_event_setting: 'hide',
          purchased_lots_setting: 'winning_lots',
          package_slug: 'run_drive',
          // Calculator Settings
          show_calculator: formData.show_calculator,
          show_discount: formData.show_discount,
          discountPercent: formData.discountPercent,
          // Note: subdealerId is auto-generated by backend after creation
          permissions: ['view_own_data', 'place_bids']
        };
        
        const result = await DashboardService.createUser(subdealerData);
        
        if (result.success) {
          showSuccess('Subdealer created successfully!');
          loadUsers(currentPage, true); // Reload data
          handleAddUserDialogClose(); // Only close on success
        } else {
          // Handle specific error messages
          let errorMessage = result.error || result.message || 'Failed to create subdealer';
          
          // Provide more user-friendly error messages
          if (errorMessage.toLowerCase().includes('manager not found')) {
            errorMessage = 'Manager not found. Please select a valid manager.';
          } else if (errorMessage.toLowerCase().includes('branch not found')) {
            errorMessage = 'Branch not found. Please select a valid branch.';
          } else if (errorMessage.toLowerCase().includes('dealer not found')) {
            errorMessage = 'Dealer not found. Please select a valid dealer.';
          } else if (errorMessage.toLowerCase().includes('username') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Username already exists. Please choose a different username.';
          } else if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already')) {
            errorMessage = 'Email already exists. Please use a different email address.';
          }
          
          console.error('Failed to create subdealer:', result);
          showError(errorMessage);
          return; // Don't close modal if failed
        }
      }
    } catch (error) {
      console.error('Failed to save subdealer:', error);
      
      // Handle network errors or unexpected errors
      let errorMessage = 'Failed to save subdealer. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      // Provide more user-friendly error messages
      if (errorMessage.toLowerCase().includes('manager not found')) {
        errorMessage = 'Manager not found. Please select a valid manager.';
      } else if (errorMessage.toLowerCase().includes('branch not found')) {
        errorMessage = 'Branch not found. Please select a valid branch.';
      } else if (errorMessage.toLowerCase().includes('dealer not found')) {
        errorMessage = 'Dealer not found. Please select a valid dealer.';
      } else if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      showError(errorMessage);
      return; // Don't close modal on error
    }
    
    // Close modal only if we reach here (success)
    handleAddUserDialogClose();
  };

  const handleRemoveUser = async (userId, name, targetUser) => {
    // Check permissions
    if (!canManageUser(targetUser)) {
      setError('You do not have permission to delete this user.');
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete subdealer " + name + "?")) {
      return;
    }

    try {
      console.log('Deleting subdealer with ID:', userId);
      const result = await DashboardService.deleteUser(userId);
      console.log('Delete result:', result);
      
      if (result.success) {
        showSuccess(result.message || 'Subdealer deleted successfully!');
        loadUsers(currentPage, true); // Reload data
      } else {
        showError(result.error || result.message || 'Failed to delete subdealer');
      }
    } catch (error) {
      console.error('Error deleting subdealer:', error);
      setError(error.message || 'Failed to delete subdealer. Please try again.');
      showError(error.message || 'Failed to delete subdealer. Please try again.');
    }
  };

  const handleSubdealerClick = (subdealer) => {
    // Use user_id for URL (matches getUserById endpoint), fallback to other IDs
    const userId = subdealer.user_id || subdealer.subdealerId || subdealer.autoIncrementId || subdealer.id;
    console.log('Navigating to subdealer with user_id:', userId, 'from subdealer object:', subdealer);
    if (userId) {
      navigate(`/${lang}/subdealers/${userId}`);
    } else {
      console.error('No valid ID found for subdealer:', subdealer);
      showError('Cannot navigate: Subdealer ID not found');
    }
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    return Object.values(searchFilters).filter(value => value && value.trim() !== '').length;
  };

  const columns = [
    {
      title: 'Subdealer',
      key: 'subdealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#1e3a5f' }}>
            {record.avatar || record.name?.substring(0, 2).toUpperCase() || 'SD'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.username || record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Dealer',
      key: 'dealer',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <ShopOutlined style={{ color: '#8c8c8c' }} />
          <Typography.Text>{record.dealerName || 'N/A'}</Typography.Text>
        </Flex>
      )
    },
    {
      title: 'Branch',
      key: 'branch',
      render: (_, record) => (
        <Tag icon={<BankOutlined />} color="blue">{record.branchName || 'N/A'}</Tag>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>{record.phone || record.employee_info?.phone || 'N/A'}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (val) => val ?? 0
    },
    {
      title: 'Won Auctions',
      dataIndex: ['stats', 'wonAuctions'],
      key: 'wonAuctions',
      render: (val) => val ?? 0
    },
    {
      title: 'Total Spent',
      key: 'totalSpent',
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>{record.stats?.totalSpent || '$0'}</span>
      )
    },
    {
      title: 'Created By',
      key: 'createdBy',
      render: (_, record) => (
        record.createdBy ? (
          <CreatorInfo createdBy={record.createdBy} createdAt={record.createdAt} variant="compact" />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        canManageUser(record) ? (
          <Space onClick={(e) => e.stopPropagation()}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); handleEditUser(record); }}
              title="Edit Subdealer"
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                const userId = record.user_id || record.subdealerId || record.autoIncrementId || record.id;
                handleRemoveUser(userId, record.name, record);
              }}
              title="Delete Subdealer"
            />
          </Space>
        ) : null
      )
    }
  ];

  return (
    <div style={{ padding: "20px" }}>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('subdealers.title')}
          {isSearching && (
            <Tag color="blue" closable onClose={clearSearch} style={{ marginLeft: 8 }}>
              {getActiveFilterCount()} filter(s) active
            </Tag>
          )}
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => loadUsers(currentPage)} title="Refresh" />
          {canCreateSubdealer() && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUserDialogOpen}>
              {t('subdealers.addSubdealer')}
            </Button>
          )}
        </Space>
      </Flex>

      {/* Quick Search */}
      <Input
        placeholder="Search by Name, Username, User ID, Employee Name, or Phone..."
        prefix={<SearchOutlined />}
        value={searchForm.quickSearch}
        onChange={(e) => handleQuickSearch(e.target.value)}
        suffix={
          searchForm.quickSearch ? (
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => handleQuickSearch('')} />
          ) : null
        }
        style={{ marginBottom: 16 }}
      />

      {/* Advanced Filters Toggle */}
      <div style={{ marginBottom: 16 }}>
        <Button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          icon={showAdvancedFilters ? <UpOutlined /> : <DownOutlined />}
        >
          Advanced Filters
        </Button>
      </div>

      {/* Advanced Filters */}
      <Collapse activeKey={showAdvancedFilters ? ['1'] : []} onChange={(keys) => setShowAdvancedFilters(keys.length > 0)} style={{ marginBottom: 16 }}>
        <Collapse.Panel header="Advanced Filters" key="1">
          <Card>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Name" value={searchForm.name} onChange={(e) => handleFormChange('name', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Username" value={searchForm.user_name} onChange={(e) => handleFormChange('user_name', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="User ID" value={searchForm.user_id} onChange={(e) => handleFormChange('user_id', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Employee First Name" value={searchForm.employee_name} onChange={(e) => handleFormChange('employee_name', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Employee Last Name" value={searchForm.employee_last_name} onChange={(e) => handleFormChange('employee_last_name', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input placeholder="Phone Number" value={searchForm.employee_phone} onChange={(e) => handleFormChange('employee_phone', e.target.value)} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Copart Status"
                  allowClear
                  style={{ width: '100%' }}
                  value={searchForm.copart_on || undefined}
                  onChange={(v) => handleFormChange('copart_on', v || '')}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' }
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="IAAI Status"
                  allowClear
                  style={{ width: '100%' }}
                  value={searchForm.iaai_on || undefined}
                  onChange={(v) => handleFormChange('iaai_on', v || '')}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' }
                  ]}
                />
              </Col>
            </Row>
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" icon={loading ? <Spin size="small" /> : <SearchOutlined />} onClick={handleAdvancedSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Apply Filters'}
              </Button>
              <Button onClick={clearSearch} disabled={loading}>
                Clear All
              </Button>
            </Space>
          </Card>
        </Collapse.Panel>
      </Collapse>

      {/* Error Display */}
      {error && (
        <Typography.Text type="danger" style={{ display: 'block', textAlign: 'center', margin: '20px 0' }}>
          {error}
        </Typography.Text>
      )}

      {/* Loading State */}
      {initialLoading && (
        <Flex justify="center" align="center" style={{ margin: '40px 0' }}>
          <Spin size="large" />
          <Typography.Text style={{ marginLeft: 16 }}>Loading users...</Typography.Text>
        </Flex>
      )}

      {/* Users Table */}
      {!initialLoading && (
        <>
          <Card style={{ marginTop: 16 }}>
            <Table
              columns={columns}
              dataSource={users}
              rowKey={(r) => r.user_id || r.subdealerId || r.autoIncrementId || r.id}
              pagination={false}
              loading={loading && !initialLoading}
              onRow={(record) => ({
                onClick: () => handleSubdealerClick(record),
                style: { cursor: 'pointer' }
              })}
              locale={{ emptyText: isSearching ? 'No subdealers found matching your search criteria.' : 'No subdealers found.' }}
            />
          </Card>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Flex justify="center" align="center" gap={16} style={{ marginTop: 24 }} vertical>
              <Pagination
                current={currentPage}
                total={pagination.totalUsers}
                pageSize={20}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => `Showing ${users.length} of ${total} users`}
              />
            </Flex>
          )}
        </>
      )}

      {/* Add/Edit Subdealer Modal */}
      <SubdealerModal 
        open={addUserOpen}
        onClose={handleAddUserDialogClose} 
        editingSubdealer={editingSubdealer}
        onSave={handleSaveSubdealer}
      />
    </div>
  );
};

export default UserPage;
